import {
  WSPanel,
  WSText,
  WSFlexBox,
  WSElement,
  WSIconList,
  WSForm,
  WSSelect,
  WSButton,
  useWSSnackbar,
  WSButtons
} from "@wingspanhq/fe-component-library";
import chunk from "lodash/chunk";
import React, { useState } from "react";
import cn from "classnames";
import { CSVLink } from "react-csv";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { useBulkUploadCollaboratorsImporter } from "../../Invoices/screens/bulkUploadCollaborators/bulkUploadCollaboratorsImporter";
import { defaultCollaboratorGroupOption } from "../../Invoices/screens/bulkUploadCollaborators/BulkUploadCollaboratorsStep2";
import { useCreateBulkCollaboratorBatch } from "../../query/payments/mutations";
import {
  useCollaboratorsQuery,
  useCollaboratorGroupsQuery,
  useListCollaboratorSettingsAdditionalData
} from "../../query/payments/queries";
import { WSQueries } from "../../query/WSQuery";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";
import styles from "./styles.module.scss";
import { UploadCollaboratorWithFormW9 } from "./NEC1099FilingUploadCollaborators";

export const NEC1099FilingImportCollaborators: React.FC<SetupFlowRouteComponentProps> = ({
  onNext
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [isFlatfileLoading, setIsFlatfileLoading] = useState(false);
  const [selectedCollaboratorGroup, setSelectedCollaboratorGroup] = useState(
    defaultCollaboratorGroupOption
  );

  const qCollaborators = useCollaboratorsQuery();
  const qCollaboratorGroups = useCollaboratorGroupsQuery();
  const [
    createBulkCollaboratorBatch,
    createBulkCollaboratorBatchMeta
  ] = useCreateBulkCollaboratorBatch();
  const bulkUploadCollaboratorsImporter = useBulkUploadCollaboratorsImporter(
    selectedCollaboratorGroup,
    {
      onClose: () => {
        setIsFlatfileLoading(false);
      },
      withFormW9: true
    }
  );

  const collaboratorSettingsAdditionalDataQuery = useListCollaboratorSettingsAdditionalData(
    {
      retry: false
    }
  );
  const additionalData = collaboratorSettingsAdditionalDataQuery.data || [];

  const onUploadSpreadsheet = async () => {
    setIsFlatfileLoading(true);
    const results = await bulkUploadCollaboratorsImporter();
    const csvCollaborators = results.validData as Array<
      UploadCollaboratorWithFormW9
    >;
    if (csvCollaborators.length === 0) {
      openSnackbar({
        duration: 5000,
        type: "warning",
        message:
          "Sorry! We could not find any collaborators in the file you uploaded"
      });
      return;
    }
    await createBulkCollaboratorBatch(
      {},
      {
        onSuccess: response => {
          setIsFlatfileLoading(false);
          onNext({
            bulkCollaboratorBatchId: response.bulkCollaboratorBatchId,
            csvCollaborators,
            selectedCollaboratorGroupId: selectedCollaboratorGroup.value,
            selectedCollaboratorGroupName: selectedCollaboratorGroup.label,
            fileName: results.fileName,
            flatFileBatchId: results.batchId
          });
        }
      }
    );
  };

  const requiredFields = [
    "Collaborator ID",
    "External Collaborator ID",
    "Email"
  ];
  const optionalFields = [
    "Phone Number",

    "Legal Business Name",
    "Employee Identification Number",
    "Social Security Number",
    "Business Type",

    "First Name",
    "Last Name",
    "Date of Birth",

    "Address Line1",
    "Address Line2",
    "City",
    "State",
    "Postal Code",
    "Country"
  ];

  additionalData.forEach(field => {
    if (field.required) {
      requiredFields.push(field.name);
    } else {
      optionalFields.push(field.name);
    }
  });

  const requiredFieldsChunks = chunk(
    requiredFields,
    Math.ceil(requiredFields.length / 2)
  );
  const optionalFieldsChunks = chunk(
    optionalFields,
    Math.ceil(optionalFields.length / 2)
  );

  const downloadExampleCollaborators = [
    {
      email: "wade@wingspan.app",
      contractorId: "",
      collaboratorId: "",

      firstName: "Wade",
      lastName: "Warren",
      phoneNumber: "",

      w9FirstName: "",
      w9LastName: "",
      dob: "",

      legalBusinessName: "",
      companyStructure: "",
      ein: "",
      ssn: "",

      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: ""
    }
  ];
  const downloadTemplateCsvProps = {
    filename: `Wingspan_Bulk_Upload_Collaborators_Template.csv`,
    headers: [
      { label: "Email", key: "email" },
      { label: "External Collaborator ID", key: "contractorId" }, // externalId
      { label: "Collaborator ID", key: "collaboratorId" },

      { label: "First Name", key: "firstName" },
      { label: "Last Name", key: "lastName" },
      { label: "Phone number", key: "phoneNumber" },

      { label: "W-9 First Name", key: "w9FirstName" },
      { label: "W-9 Last Name", key: "w9LastName" },
      { label: "Date of Birth", key: "dob" },
      {
        label: "W-9 Legal Business Name",
        key: "legalBusinessName"
      },
      { label: "W-9 Company Structure", key: "companyStructure" },
      { label: "EIN", key: "ein" },
      { label: "SSN", key: "ssn" },

      { label: "Address Line1", key: "addressLine1" },
      { label: "Address Line2", key: "addressLine2" },
      { label: "City", key: "city" },
      { label: "State", key: "state" },
      { label: "Postal Code", key: "postalCode" },
      { label: "Country", key: "country" }
    ],
    data: downloadExampleCollaborators
  };

  return (
    <NEC1099FilingContainer title="Import Collaborators">
      <WSQueries queries={{ qCollaborators, qCollaboratorGroups }}>
        {({ qCollaboratorGroups: { data: collaboratorGroups } }) => {
          const collaboratorGroupOptions = [
            defaultCollaboratorGroupOption,
            ...collaboratorGroups.map(collaboratorGroup => ({
              label: collaboratorGroup.name,
              value: (collaboratorGroup as any).collaboratorGroupId
            }))
          ];

          const onSelectCollaboratorGroup = (value: any) => {
            const collaboratorGroup = collaboratorGroupOptions.find(
              cg => cg.value === value
            );
            setSelectedCollaboratorGroup(
              collaboratorGroup ?? defaultCollaboratorGroupOption
            );
          };

          return (
            <>
              <WSText.Paragraph>
                Don't worry if your spreadsheet has other details, but please
                make sure it contains the following columns.
              </WSText.Paragraph>

              <WSText.Heading4 my="S">
                Please Provide One of the Following ID Types
              </WSText.Heading4>
              <WSFlexBox mb="M">
                <WSElement style={{ flex: 1 }}>
                  <WSIconList.Checks items={requiredFieldsChunks[0]} mb="M" />
                </WSElement>
                <WSElement style={{ flex: 1 }}>
                  <WSIconList.Checks items={requiredFieldsChunks[1]} mb="M" />
                </WSElement>
              </WSFlexBox>

              <WSText.Heading4 my="S">Optional</WSText.Heading4>
              <WSFlexBox mb="M">
                <WSElement style={{ flex: 1 }}>
                  <WSIconList.Checks items={optionalFieldsChunks[0]} mb="M" />
                </WSElement>
                <WSElement style={{ flex: 1 }}>
                  <WSIconList.Checks items={optionalFieldsChunks[1]} mb="M" />
                </WSElement>
              </WSFlexBox>

              {collaboratorGroups.length > 0 && (
                <WSForm
                  defaultValues={{
                    collaboratorGroupId: selectedCollaboratorGroup.value
                  }}
                >
                  <WSForm.Field
                    name="collaboratorGroupId"
                    label="Collaborator group"
                    component={WSSelect}
                    componentProps={{
                      options: collaboratorGroupOptions,
                      onChange: onSelectCollaboratorGroup
                    }}
                    my="M"
                  />
                </WSForm>
              )}

              <WSButtons mt="XL">
                <CSVLink {...downloadTemplateCsvProps}>
                  <WSButton.Secondary
                    className={cn(styles.actionBtn, styles.downloadBtn)}
                    fullWidth
                    icon="download"
                    p="S"
                  >
                    Download template
                  </WSButton.Secondary>
                </CSVLink>

                <WSButton.Primary
                  className={cn(styles.actionBtn, styles.uploadBtn)}
                  fullWidth
                  icon="upload"
                  p="S"
                  data-testid="importCollaborators"
                  onClick={onUploadSpreadsheet}
                  loading={
                    isFlatfileLoading ||
                    createBulkCollaboratorBatchMeta.isLoading
                  }
                >
                  Import Collaborators
                </WSButton.Primary>
              </WSButtons>
            </>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
